import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CustomToast from '../../components/Redesing/custom-toast';
import { TagPicker } from './TagPicker';
import { ChatTagGlossary, HardwareFamily } from './tagGlossary';

interface Props {
  open: boolean;
  riderName: string;
  /** The session's tags as ant has them, live while the dialog is open. */
  tags: string[];
  /** CS's glossary; null while it loads or when it could not be read. */
  glossary: ChatTagGlossary | null;
  glossaryError?: string | null;
  /** The session's rental family, for the picker's filter; undefined while unknown. */
  hardwareFamily: HardwareFamily | null | undefined;
  /** Takes a tag the session already carries off it, through ant, as the agent clicks. */
  onRemoveTag: (tag: string) => Promise<void>;
  onCancel: () => void;
  /**
   * Resolves when ant has closed the session with the note and the tags
   * added in this dialog (ant appends them in the same write); rejects to
   * keep the dialog open.
   */
  onConfirm: (resolutionNote: string, addedTags: string[]) => Promise<void>;
}

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : String(error ?? '');

/**
 * The one way to end a conversation: the header icon and the panel button
 * both open this dialog. It carries the tag picker, prefilled from the
 * session's tags and refreshed from the subscription while open: removals
 * go to ant as the agent clicks, additions travel with the close, so a tag
 * a colleague adds meanwhile survives. It warns when the session is
 * untagged and lets the agent close anyway.
 */
export function EndConversationDialog({
  open,
  riderName,
  tags,
  glossary,
  glossaryError = null,
  hardwareFamily,
  onRemoveTag,
  onCancel,
  onConfirm,
}: Props) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  /** Tags picked here, not yet on the session: sent with the close. */
  const [picked, setPicked] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setNote('');
      setPicked([]);
    }
  }, [open]);

  // A tag picked here that the session now carries (a colleague added it) is
  // the session's, not an addition.
  const added = useMemo(() => picked.filter((id) => !tags.includes(id)), [picked, tags]);
  const selected = useMemo(() => [...tags, ...added], [tags, added]);

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm(note.trim(), added);
    } catch (_) {
      // The caller has said what went wrong; the dialog stays open to retry.
    } finally {
      setBusy(false);
    }
  };

  const removeTag = async (id: string) => {
    // The pick is forgotten either way. A tag the session carries (never
    // picked here, or picked and meanwhile added by a colleague) comes off
    // through ant as well; keeping the pick would put it back on the close.
    setPicked((prev) => prev.filter((other) => other !== id));
    if (!tags.includes(id)) return;
    try {
      await onRemoveTag(id);
    } catch (error) {
      CustomToast({
        type: 'error',
        message: `Could not remove the tag. ${errorText(error)}`,
        duration: 6000,
      });
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>End conversation</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The conversation with {riderName} leaves the queue and becomes read-only. If the
          rider writes again, their message opens a new conversation.
        </DialogContentText>
        <Stack gap={2}>
          <Stack gap={1}>
            <Typography variant="subtitle2">Tags</Typography>
            <TagPicker
              glossary={glossary}
              glossaryError={glossaryError}
              selected={selected}
              family={hardwareFamily}
              disabled={busy}
              onAdd={async (id) => {
                setPicked((prev) => (prev.includes(id) ? prev : [...prev, id]));
              }}
              onRemove={removeTag}
            />
            {selected.length === 0 && (
              <Alert severity="warning" sx={{ py: 0 }}>
                No tag yet. The conversation can still be ended; CS&apos;s issue report
                counts tagged chats.
              </Alert>
            )}
          </Stack>
          <TextField
            label="Resolution note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            disabled={busy}
            placeholder="e.g. Unlocked remotely, rider continued the trip"
            helperText="Only the support team sees this note."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={confirm}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          End conversation
        </Button>
      </DialogActions>
    </Dialog>
  );
}
